import { useId, useRef, useState } from 'react';
import { Check, ChevronDown } from 'lucide-react';
import type { KeyboardEvent, ReactElement } from 'react';
import type { Theme } from '../types';

export interface SessionExerciseItem {
  readonly name: string;
  readonly completedSets: number;
  readonly totalSets: number;
  readonly isComplete: boolean;
}

interface SessionExerciseDropdownProps {
  exercises: readonly SessionExerciseItem[];
  activeIndex: number;
  pendingCount: number;
  theme: Theme;
  onSelect: (index: number) => void;
}

type ExerciseStatus = 'complete' | 'active' | 'pending';

function getExerciseStatus(isComplete: boolean, isActive: boolean): ExerciseStatus {
  if (isComplete) return 'complete';
  if (isActive) return 'active';
  return 'pending';
}

function getStatusLabel(status: ExerciseStatus): string {
  switch (status) {
    case 'complete':
      return 'Completo';
    case 'active':
      return 'Actual';
    case 'pending':
      return 'Pendiente';
  }
}

function getStatusClassName(status: ExerciseStatus): string {
  switch (status) {
    case 'complete':
      return 'bg-green-500 text-white';
    case 'active':
      return 'bg-white/20 ring-1 ring-white/70';
    case 'pending':
      return 'bg-stone-400/80';
  }
}

function getPendingLabel(pendingCount: number): string {
  if (pendingCount === 0) return 'Todo completo';
  if (pendingCount === 1) return '1 pendiente';
  return `${pendingCount} pendientes`;
}

export function SessionExerciseDropdown({
  exercises,
  activeIndex,
  pendingCount,
  theme,
  onSelect,
}: SessionExerciseDropdownProps): ReactElement {
  const [isOpen, setIsOpen] = useState(false);
  const panelId = useId();
  const triggerRef = useRef<HTMLButtonElement>(null);

  const closeAndRestoreFocus = () => {
    setIsOpen(false);
    triggerRef.current?.focus();
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key !== 'Escape' || !isOpen) return;
    event.preventDefault();
    event.stopPropagation();
    closeAndRestoreFocus();
  };

  const handleSelect = (index: number) => {
    onSelect(index);
    closeAndRestoreFocus();
  };

  return (
    <div
      className={`rounded-[20px] ${theme.card} border ${theme.border}`}
      onKeyDown={handleKeyDown}
    >
      <button
        ref={triggerRef}
        type='button'
        onClick={() => setIsOpen((current) => !current)}
        aria-expanded={isOpen}
        aria-controls={panelId}
        className={`w-full min-h-14 px-4 py-3 sm:px-5 rounded-[20px] flex items-center justify-between gap-3 text-left ${theme.hover}`}
      >
        <span className='min-w-0'>
          <span className='block text-sm sm:text-base font-bold'>
            Ejercicios de la sesión
          </span>
          <span className={`block text-xs sm:hidden truncate ${theme.textSecondary}`}>
            {exercises[activeIndex]?.name ?? 'Seleccionar ejercicio'}
          </span>
        </span>
        <span className='flex items-center gap-2 flex-shrink-0'>
          <span className={`text-xs sm:text-sm ${theme.textSecondary}`}>
            {getPendingLabel(pendingCount)}
          </span>
          <ChevronDown
            aria-hidden='true'
            className={`w-4 h-4 transition-transform duration-200 ${
              isOpen ? 'rotate-180' : ''
            }`}
          />
        </span>
      </button>

      {isOpen ? (
        <div
          id={panelId}
          role='region'
          aria-label='Seleccionar ejercicio de la sesión'
          className={`mx-2 mb-2 pt-1 border-t ${theme.border}`}
        >
          {exercises.map((exercise, index) => {
            const isActive = index === activeIndex;
            const status = getExerciseStatus(exercise.isComplete, isActive);
            return (
              <button
                key={`${exercise.name}-${index}`}
                type='button'
                onClick={() => handleSelect(index)}
                aria-current={isActive ? 'step' : undefined}
                className={`w-full min-h-12 px-3 py-2.5 rounded-xl flex items-center justify-between gap-3 text-left transition-colors ${
                  isActive ? 'bg-[#b4532a] text-white' : theme.hover
                }`}
              >
                <span className='flex items-center gap-2.5 min-w-0'>
                  <span
                    aria-hidden='true'
                    className={`w-4 h-4 rounded-full flex-shrink-0 flex items-center justify-center ${getStatusClassName(status)}`}
                  >
                    {exercise.isComplete ? <Check className='w-3 h-3' /> : null}
                  </span>
                  <span className='sr-only'>
                    {getStatusLabel(status)}
                  </span>
                  <span className='font-semibold text-sm sm:text-base truncate'>
                    {exercise.name}
                  </span>
                </span>
                <span
                  className={`text-xs tabular-nums flex-shrink-0 ${
                    isActive ? 'text-white/80' : theme.textSecondary
                  }`}
                >
                  {exercise.completedSets}/{exercise.totalSets}
                </span>
              </button>
            );
          })}
        </div>
      ) : null}
    </div>
  );
}
